import { defineStore } from "pinia";
import { Capacitor } from "@capacitor/core";
const isPushDebugEnabled = () => {
    if (!Capacitor.isNativePlatform()) {
        return false;
    }
    if (process.env.NODE_ENV === "production") {
        if (typeof localStorage !== "undefined" &&
            localStorage.getItem("PUSH_DEBUG_ENABLED") === "1") {
            return true;
        }
        return false;
    }
    if (typeof localStorage !== "undefined" &&
        localStorage.getItem("PUSH_DEBUG_DISABLED") === "1") {
        return false;
    }
    return true;
};
const MAX_EVENTS = 25;
export const usePushDebugStore = defineStore("pushDebug", {
    state: () => ({
        enabled: isPushDebugEnabled(),
        listenersReady: false,
        fcmToken: "",
        fcmTokenAt: "",
        lastSubscribeOkAt: "",
        lastSubscribeError: "",
        events: [],
        bannerVisible: false,
        bannerTitle: "",
        bannerBody: "",
        panelExpanded: false,
    }),
    getters: {
        tokenLabel() {
            return Capacitor.getPlatform() === "ios" ? "APNs" : "FCM";
        },
        fcmTokenPreview(state) {
            const t = state.fcmToken;
            const label = Capacitor.getPlatform() === "ios" ? "APNs" : "FCM";
            if (!t)
                return `(sin token ${label} en app)`;
            if (t.length <= 24)
                return t;
            return `${t.slice(0, 12)}…${t.slice(-8)}`;
        },
        diagnosticText(state) {
            const lines = [
                "=== MiCentrum Push Debug ===",
                `platform: ${Capacitor.getPlatform()}`,
                `listenersReady: ${state.listenersReady}`,
                `fcmToken: ${state.fcmToken || "(vacío)"}`,
                `fcmTokenAt: ${state.fcmTokenAt || "-"}`,
                `lastSubscribeOk: ${state.lastSubscribeOkAt || "-"}`,
                `lastSubscribeError: ${state.lastSubscribeError || "-"}`,
                "",
                "--- eventos (más reciente primero) ---",
            ];
            for (const e of state.events) {
                lines.push(`[${e.at}] ${e.phase}: ${e.summary}`);
                if (e.detail) {
                    lines.push(e.detail);
                }
                lines.push("");
            }
            return lines.join("\n");
        },
    },
    actions: {
        recordEvent(phase, summary, detail) {
            if (!this.enabled) {
                return;
            }
            const event = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                at: new Date().toISOString(),
                phase,
                summary,
                detail,
            };
            this.events.unshift(event);
            if (this.events.length > MAX_EVENTS) {
                this.events.length = MAX_EVENTS;
            }
            console.log(`[PushDebug] ${phase}:`, summary, detail ?? "");
        },
        setListenersReady() {
            if (!this.enabled) {
                return;
            }
            this.listenersReady = true;
            this.recordEvent("listeners_ready", "Listeners Capacitor push registrados");
        },
        setFcmToken(token) {
            if (!this.enabled || !token) {
                return;
            }
            this.fcmToken = token;
            this.fcmTokenAt = new Date().toISOString();
            this.recordEvent("registration", `Token ${Capacitor.getPlatform() === "ios" ? "APNs" : "FCM"} (${token.length} chars)`, token);
        },
        recordSubscribeSuccess(payload) {
            if (!this.enabled) {
                return;
            }
            this.lastSubscribeOkAt = new Date().toISOString();
            this.lastSubscribeError = "";
            let detail = "";
            try {
                detail = JSON.stringify(payload, null, 2);
            }
            catch {
                detail = String(payload);
            }
            this.recordEvent("subscribe_ok", "Suscripción push en backend OK", detail);
        },
        recordSubscribeError(message) {
            if (!this.enabled) {
                return;
            }
            this.lastSubscribeError = message;
            this.recordEvent("subscribe_err", "Error al suscribir push", message);
        },
        showPushBanner(title, body) {
            if (!this.enabled) {
                return;
            }
            this.bannerTitle = title;
            this.bannerBody = body;
            this.bannerVisible = true;
            window.setTimeout(() => {
                if (this.bannerTitle === title && this.bannerBody === body) {
                    this.bannerVisible = false;
                }
            }, 12000);
        },
        togglePanel() {
            this.panelExpanded = !this.panelExpanded;
        },
        async copyDiagnostics() {
            const text = this.diagnosticText;
            try {
                if (navigator.clipboard?.writeText) {
                    await navigator.clipboard.writeText(text);
                    return true;
                }
            }
            catch {
                /* fallback */
            }
            try {
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.setAttribute("readonly", "");
                ta.style.position = "fixed";
                ta.style.left = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                const ok = document.execCommand("copy");
                document.body.removeChild(ta);
                return ok;
            }
            catch {
                return false;
            }
        },
    },
});
